import {
	IconChevronLeft,
	IconChevronRight,
	IconFilterOff,
	IconSearch,
} from "@tabler/icons-react";
import {
	Button,
	Card,
	Empty,
	Flex,
	Input,
	Select,
	Tooltip,
	Typography,
	theme,
} from "antd";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { CustomIcon } from "./base";

const ALL = "ALL";

export type CardViewerFilter<T> = {
	/** Unique key for this filter's dropdown state. */
	key: string;
	/** Label for the "no filter" option, e.g. "All statuses". */
	allLabel: string;
	options: { value: string; label: string }[];
	/** Keep the item when it matches the chosen (non-"all") value. */
	predicate: (item: T, value: string) => boolean;
	width?: number;
};

export type CardViewerSort<T> = {
	value: string;
	label: string;
	compare: (a: T, b: T) => number;
};

/**
 * Controlled server-side mode. When provided, CardViewer does NO client filtering/sorting —
 * it renders `items` as-is and forwards toolbar changes + Next/Prev to these callbacks.
 * Filter/sort option lists still come from `filters`/`sorts`; their predicate/compare are unused.
 */
export interface CardViewerServer {
	search: string;
	onSearchChange: (v: string) => void;
	filterValues: Record<string, string | undefined>;
	onFilterChange: (key: string, value: string | undefined) => void;
	sortValue?: string;
	onSortChange: (value: string) => void;
	totalCount: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	onNextPage: () => void;
	onPreviousPage: () => void;
	pageSize?: number;
	/** 0-based current page index, for the "Page X of Y" footer. */
	pageIndex?: number;
}

interface CardViewerProps<T> {
	items: T[];
	getKey: (item: T) => string;
	renderCard: (item: T) => ReactNode;
	onOpen?: (item: T) => void;
	/** Text to match the search box against; enables search when provided. */
	searchText?: (item: T) => string;
	searchPlaceholder?: string;
	filters?: CardViewerFilter<T>[];
	sorts?: CardViewerSort<T>[];
	minCardWidth?: number;
	emptyText?: string;
	/** Extra controls (e.g. a create button) rendered right-aligned in the toolbar. */
	extra?: ReactNode;
	/** When set, the parent drives search/filter/sort/pagination server-side. */
	server?: CardViewerServer;
	/** Seeds each filter's initial value (client mode only), e.g. to pre-scope by context. */
	initialFilterValues?: Record<string, string>;
	/** When set, a matching card gets the same selected border/background SegmentedSelection
	 *  uses — for a multi-select browse mode driven by the parent. */
	isSelected?: (item: T) => boolean;
	/** Paginates client-mode results at this many per page (ignored with `server`, which
	 *  already paginates on the caller's behalf). Omitted, every filtered result renders. */
	pageSize?: number;
}

/** Toolbar (search / filter / sort) over a responsive, wrapping card grid. */
export function CardViewer<T>({
	items,
	getKey,
	renderCard,
	onOpen,
	searchText,
	searchPlaceholder = "Search",
	filters = [],
	sorts = [],
	minCardWidth = 280,
	emptyText = "No results match your filters",
	extra,
	server,
	initialFilterValues,
	isSelected,
	pageSize: clientPageSize,
}: CardViewerProps<T>) {
	const { token } = theme.useToken();
	const [query, setQuery] = useState("");
	const [filterValues, setFilterValues] = useState<Record<string, string>>(
		initialFilterValues ?? {},
	);
	const [sortValue, setSortValue] = useState(sorts[0]?.value);
	const [clientPage, setClientPage] = useState(0);

	// Current control values come from the server prop when controlled, else local state.
	const q = server ? server.search : query;
	const fVals = server ? server.filterValues : filterValues;
	const sVal = server ? server.sortValue : sortValue;

	const onQ = (v: string) => (server ? server.onSearchChange(v) : setQuery(v));
	const onF = (key: string, v: string) =>
		server
			? server.onFilterChange(key, v === ALL ? undefined : v)
			: setFilterValues((prev) => ({ ...prev, [key]: v }));
	const onS = (v: string) =>
		server ? server.onSortChange(v) : setSortValue(v);

	const isDirty =
		(q ?? "") !== "" ||
		Object.values(fVals).some((v) => v && v !== ALL) ||
		sVal !== sorts[0]?.value;

	const reset = () => {
		onQ("");
		for (const f of filters) onF(f.key, ALL);
		if (sorts[0]) onS(sorts[0].value);
	};

	// Client-mode filtering/sorting (skipped entirely when server-controlled).
	const clientVisible = useMemo(() => {
		const needle = query.trim().toLowerCase();
		const list = items.filter((item) => {
			if (
				needle &&
				searchText &&
				!searchText(item).toLowerCase().includes(needle)
			)
				return false;
			for (const f of filters) {
				const v = filterValues[f.key] ?? ALL;
				if (v !== ALL && !f.predicate(item, v)) return false;
			}
			return true;
		});
		const sort = sorts.find((s) => s.value === sortValue);
		return sort ? [...list].sort(sort.compare) : list;
	}, [items, query, filterValues, sortValue, searchText, filters, sorts]);

	// Client-mode pagination re-starts at page 1 whenever the filtered set could change.
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset-on-change, not a read
	useEffect(() => {
		setClientPage(0);
	}, [query, filterValues, sortValue]);

	const clientPaged = clientPageSize
		? clientVisible.slice(
				clientPage * clientPageSize,
				(clientPage + 1) * clientPageSize,
			)
		: clientVisible;

	const visible = server ? items : clientPaged;
	const hasToolbar =
		!!searchText || filters.length > 0 || sorts.length > 0 || !!extra;
	const pageSize = server?.pageSize ?? clientPageSize ?? 50;

	const pagination = server
		? {
				totalCount: server.totalCount,
				hasNextPage: server.hasNextPage,
				hasPreviousPage: server.hasPreviousPage,
				onNextPage: server.onNextPage,
				onPreviousPage: server.onPreviousPage,
				pageIndex: server.pageIndex ?? 0,
			}
		: clientPageSize
			? {
					totalCount: clientVisible.length,
					hasNextPage: (clientPage + 1) * clientPageSize < clientVisible.length,
					hasPreviousPage: clientPage > 0,
					onNextPage: () => setClientPage((p) => p + 1),
					onPreviousPage: () => setClientPage((p) => Math.max(0, p - 1)),
					pageIndex: clientPage,
				}
			: null;

	return (
		<Flex vertical gap={16}>
			{hasToolbar && (
				<Flex wrap gap={8} align="center">
					{searchText && (
						<Input
							allowClear
							placeholder={searchPlaceholder}
							prefix={<CustomIcon icon={IconSearch} />}
							value={q}
							onChange={(e) => onQ(e.target.value)}
							style={{ maxWidth: 280 }}
						/>
					)}
					{filters.map((f) => (
						<Select
							key={f.key}
							value={fVals[f.key] ?? ALL}
							onChange={(v) => onF(f.key, v)}
							style={{ minWidth: f.width ?? 140 }}
							options={[{ value: ALL, label: f.allLabel }, ...f.options]}
						/>
					))}
					{sorts.length > 0 && (
						<Select
							value={sVal}
							onChange={onS}
							style={{ minWidth: 130 }}
							options={sorts.map((s) => ({ value: s.value, label: s.label }))}
						/>
					)}
					{isDirty && (
						<Tooltip title="Reset filters">
							<Button
								type="text"
								icon={<CustomIcon icon={IconFilterOff} />}
								onClick={reset}
							/>
						</Tooltip>
					)}
					{extra && <div style={{ marginLeft: "auto" }}>{extra}</div>}
				</Flex>
			)}

			{visible.length === 0 ? (
				<Empty description={emptyText} />
			) : (
				<div
					style={{
						display: "grid",
						gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`,
						gap: 16,
						width: "100%",
					}}
				>
					{visible.map((item) => {
						const selected = isSelected?.(item) ?? false;
						return (
							<Card
								key={getKey(item)}
								hoverable
								onClick={onOpen ? () => onOpen(item) : undefined}
								style={{
									height: "100%",
									cursor: onOpen ? "pointer" : "default",
									borderColor: selected ? token.colorPrimary : undefined,
									backgroundColor: selected ? token.colorPrimaryBg : undefined,
									transition: "border-color 0.2s, background-color 0.2s",
								}}
								styles={{ body: { padding: 16 } }}
							>
								{renderCard(item)}
							</Card>
						);
					})}
				</div>
			)}

			{pagination && (pagination.hasNextPage || pagination.hasPreviousPage) && (
				<Flex justify="flex-end" align="center" gap={8}>
					<Typography.Text type="secondary" style={{ fontSize: "0.8rem" }}>
						Page {pagination.pageIndex + 1} of{" "}
						{Math.max(1, Math.ceil(pagination.totalCount / pageSize))} -{" "}
						{pagination.totalCount} items
					</Typography.Text>
					<Button
						size="small"
						type="text"
						disabled={!pagination.hasPreviousPage}
						onClick={pagination.onPreviousPage}
						icon={<CustomIcon icon={IconChevronLeft} />}
					>
						Previous
					</Button>
					<Button
						size="small"
						type="text"
						disabled={!pagination.hasNextPage}
						onClick={pagination.onNextPage}
						iconPlacement="end"
						icon={<CustomIcon icon={IconChevronRight} />}
					>
						Next
					</Button>
				</Flex>
			)}
		</Flex>
	);
}

/** Standard card layout: bold title + top-right extra, optional 2-line description, optional footer row. */
export function CardBody({
	title,
	leading,
	extra,
	description,
	footer,
}: {
	title: string;
	/** Rendered before the title, on the same row (e.g. a selection checkbox). */
	leading?: ReactNode;
	extra?: ReactNode;
	description?: ReactNode;
	footer?: ReactNode;
}) {
	return (
		<Flex vertical gap={8} style={{ height: "100%" }}>
			<Flex justify="space-between" align="flex-start" gap={8}>
				<Flex align="center" gap={8} style={{ minWidth: 0 }}>
					{leading}
					<Typography.Text strong ellipsis={{ tooltip: title }}>
						{title}
					</Typography.Text>
				</Flex>
				{extra}
			</Flex>
			{description !== undefined && (
				<Typography.Paragraph
					type="secondary"
					ellipsis={{ rows: 2 }}
					style={{ marginBottom: 0, minHeight: 42 }}
				>
					{description}
				</Typography.Paragraph>
			)}
			{footer && (
				<Flex justify="space-between" align="center" gap={8}>
					{footer}
				</Flex>
			)}
		</Flex>
	);
}
